'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';

interface PasswordStrengthProps {
    password: string;
    showRequirements?: boolean;
}

export const getPasswordStrength = (password: string) => {
    const checks = {
        length: password.length >= 8,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const passed = Object.values(checks).filter(Boolean).length;

    let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
    let color = 'bg-red-400';
    let label = 'Weak';

    if (passed >= 5) {
        strength = 'strong';
        color = 'bg-green-400';
        label = 'Strong';
    } else if (passed >= 4) {
        strength = 'good';
        color = 'bg-emerald-400';
        label = 'Good';
    } else if (passed >= 3) {
        strength = 'fair';
        color = 'bg-amber-400';
        label = 'Fair';
    }

    return { checks, passed, strength, color, label };
};

export default function PasswordStrength({ password, showRequirements = true }: PasswordStrengthProps) {
    const { checks, passed, color, label } = getPasswordStrength(password);

    if (!password) return null;

    const requirements = [
        { key: 'length', label: 'At least 8 characters', met: checks.length },
        { key: 'lowercase', label: 'One lowercase letter', met: checks.lowercase },
        { key: 'uppercase', label: 'One uppercase letter', met: checks.uppercase },
        { key: 'number', label: 'One number', met: checks.number },
        { key: 'special', label: 'One special character', met: checks.special },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3"
        >
            <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                    <span className="text-[#d8c59f]/55 uppercase tracking-[0.16em]">Password strength</span>
                    <span className={`font-medium ${label === 'Strong' ? 'text-green-400' :
                            label === 'Good' ? 'text-emerald-400' :
                                label === 'Fair' ? 'text-amber-400' : 'text-red-400'
                        }`}>{label}</span>
                </div>
                <div className="h-1.5 bg-[#e8d7b0]/10 rounded-full overflow-hidden">
                    <motion.div
                        className={`h-full ${color} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${(passed / 5) * 100}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            {showRequirements && (
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                    {requirements.map((req) => (
                        <div
                            key={req.key}
                            className={`flex items-center gap-1.5 ${req.met ? 'text-green-400' : 'text-[#d8c59f]/45'}`}
                        >
                            {req.met ? (
                                <Check size={12} className="shrink-0" />
                            ) : (
                                <X size={12} className="shrink-0 opacity-50" />
                            )}
                            <span className="truncate">{req.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
}
